import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

/**
 * Öffentliche Newsletter-Abmeldung — Niwero-Look, ohne Login/App-Shell.
 * /bauos-Newsletter(?u=token|&ok=1|&already=1|&error=1|&preview=1)
 */
export default function PublicUnsubscribe() {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasToken = !!(searchParams.get('u') || searchParams.get('token'));
  const [status, setStatus] = useState(() => {
    if (searchParams.get('error') === '1') return 'error';
    if (searchParams.get('already') === '1') return 'already';
    if (searchParams.get('ok') === '1') return 'ok';
    if (hasToken) return 'loading';
    return 'idle';
  });

  useEffect(() => {
    const raw = String(searchParams.get('u') || searchParams.get('token') || '').trim();
    if (!raw) return undefined;

    let cancelled = false;
    const token = raw.replace(/[^a-fA-F0-9]/g, '');
    if (!token || token.length < 16) {
      setStatus('error');
      setSearchParams({ error: '1' }, { replace: true });
      return undefined;
    }

    (async () => {
      const applyResult = (data) => {
        if (cancelled) return;
        const already = !!data?.already;
        setStatus(already ? 'already' : 'ok');
        setSearchParams(already ? { already: '1' } : { ok: '1' }, { replace: true });
      };
      const applyError = () => {
        if (cancelled) return;
        setStatus('error');
        setSearchParams({ error: '1' }, { replace: true });
      };

      try {
        const res = await axios.post(
          `/api/u/${encodeURIComponent(token)}`,
          {},
          { headers: { Accept: 'application/json' } }
        );
        applyResult(res.data);
      } catch (err) {
        if (err.response?.status === 404) {
          applyError();
          return;
        }
        try {
          const res = await axios.get(`/api/u/${encodeURIComponent(token)}`, {
            headers: { Accept: 'application/json' },
            validateStatus: (s) => s >= 200 && s < 500,
          });
          if (res.status >= 400 || res.data?.success === false) {
            applyError();
            return;
          }
          applyResult(res.data);
        } catch (_) {
          applyError();
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, setSearchParams]);

  const title =
    status === 'loading'
      ? 'Abmeldung wird verarbeitet…'
      : status === 'error'
        ? 'Abmeldung nicht möglich'
        : status === 'already'
          ? 'Du bist abgemeldet'
          : status === 'ok'
            ? 'Erfolgreich abgemeldet'
            : 'Newsletter abmelden';

  const message =
    status === 'loading'
      ? 'Einen Moment bitte.'
      : status === 'error'
        ? 'Der Abmelde-Link ist ungültig oder abgelaufen. Bitte nutze den Link aus der letzten E-Mail.'
        : status === 'already'
          ? 'Du bist bereits vom Newsletter und weiteren Kampagnen-Nachrichten abgemeldet. Es ist nichts weiter zu tun.'
          : status === 'ok'
            ? 'Du wurdest erfolgreich vom Newsletter und weiteren Kampagnen-Nachrichten ausgetragen. Du erhältst keine weiteren E-Mails mehr.'
            : 'Öffne den Abmelde-Link aus deiner E-Mail — damit trägst du dich zuverlässig aus.';

  const pill =
    status === 'error'
      ? 'Fehler'
      : status === 'already'
        ? 'Bereits erledigt'
        : status === 'loading'
          ? 'Wird verarbeitet'
          : status === 'ok'
            ? 'Abgemeldet'
            : 'Newsletter';

  return (
    <div className="niw-unsub">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700&display=swap');
        .niw-unsub {
          --bg: #070708;
          --fg: #f5f5f4;
          --muted: rgba(245,245,244,0.62);
          --orange: #ff5a1f;
          --orange-soft: rgba(255,90,31,0.22);
          --line: rgba(255,255,255,0.12);
          --card: rgba(18,18,20,0.72);
          margin: 0;
          min-height: 100vh;
          font-family: Manrope, system-ui, sans-serif;
          color: var(--fg);
          background:
            radial-gradient(90% 55% at 12% 8%, rgba(255,90,31,0.28), transparent 55%),
            radial-gradient(70% 45% at 88% 18%, rgba(56,140,255,0.14), transparent 50%),
            radial-gradient(60% 40% at 70% 90%, rgba(255,90,31,0.12), transparent 55%),
            linear-gradient(165deg, #0b0b0d 0%, #070708 45%, #101014 100%);
          background-attachment: fixed;
        }
        .niw-unsub * { box-sizing: border-box; }
        .niw-shell {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          padding: 1.1rem 1rem 1.5rem;
        }
        .niw-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
          width: min(56rem, 100%);
          margin: 0 auto 2.5rem;
          padding: 0.55rem 0.7rem 0.55rem 1rem;
          border: 1px solid var(--line);
          border-radius: 999px;
          background: rgba(12,12,14,0.55);
          backdrop-filter: blur(14px);
        }
        .niw-brand {
          display: inline-flex;
          align-items: center;
          gap: 0.55rem;
          flex-shrink: 0;
          text-decoration: none;
          color: var(--fg);
          font-weight: 600;
          letter-spacing: -0.02em;
          font-size: 1.02rem;
        }
        .niw-dots { display: inline-flex; gap: 0.22rem; }
        .niw-dots span {
          width: 0.42rem;
          height: 0.42rem;
          border-radius: 999px;
          background: var(--orange);
          box-shadow: 0 0 12px var(--orange-soft);
        }
        .niw-nav {
          display: flex;
          align-items: center;
          gap: 0.15rem;
          min-width: 0;
          overflow-x: auto;
          scrollbar-width: none;
          -ms-overflow-style: none;
          mask-image: linear-gradient(90deg, transparent, #000 8px, #000 calc(100% - 8px), transparent);
        }
        .niw-nav::-webkit-scrollbar { display: none; }
        .niw-nav a {
          flex-shrink: 0;
          padding: 0.4rem 0.55rem;
          border-radius: 999px;
          color: var(--muted);
          text-decoration: none;
          font-size: 0.8rem;
          font-weight: 500;
          white-space: nowrap;
          transition: color 0.15s ease, background 0.15s ease;
        }
        .niw-nav a:hover {
          color: var(--fg);
          background: rgba(255,255,255,0.06);
        }
        .niw-top-link {
          flex-shrink: 0;
          padding: 0.45rem 0.85rem;
          border-radius: 999px;
          color: var(--fg);
          text-decoration: none;
          font-size: 0.8rem;
          font-weight: 600;
          background: rgba(255,255,255,0.06);
          border: 1px solid var(--line);
          white-space: nowrap;
        }
        .niw-top-link:hover { background: rgba(255,255,255,0.1); }
        @media (max-width: 720px) {
          .niw-top {
            flex-wrap: wrap;
            border-radius: 1.15rem;
            padding: 0.7rem 0.8rem;
          }
          .niw-nav {
            order: 3;
            width: 100%;
            mask-image: none;
            padding-top: 0.15rem;
          }
        }
        .niw-stage { flex: 1; display: grid; place-items: center; }
        .niw-main {
          width: min(40rem, 100%);
          padding: clamp(1.5rem, 3.5vw, 2.25rem);
          border-radius: 1.35rem;
          border: 1px solid var(--line);
          background: var(--card);
          box-shadow: 0 30px 80px rgba(0,0,0,0.45);
          backdrop-filter: blur(18px);
        }
        .niw-pill {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          margin-bottom: 1rem;
          padding: 0.35rem 0.75rem;
          border-radius: 999px;
          border: 1px solid var(--line);
          background: rgba(255,255,255,0.04);
          color: var(--muted);
          font-size: 0.72rem;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }
        .niw-pill i {
          width: 0.4rem;
          height: 0.4rem;
          border-radius: 999px;
          background: var(--orange);
          box-shadow: 0 0 10px var(--orange);
        }
        .niw-main h1 {
          margin: 0 0 0.85rem;
          font-family: Manrope, system-ui, sans-serif;
          font-size: clamp(1.85rem, 4.2vw, 2.65rem);
          font-weight: 400;
          letter-spacing: -0.025em;
          line-height: 1.08;
        }
        .niw-main p {
          margin: 0;
          max-width: 32rem;
          color: var(--muted);
          font-size: 1rem;
          line-height: 1.65;
        }
        .niw-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          margin-top: 1.6rem;
        }
        .niw-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 2.65rem;
          padding: 0.65rem 1.1rem;
          border-radius: 999px;
          text-decoration: none;
          font-weight: 600;
          font-size: 0.92rem;
        }
        .niw-btn-primary {
          background: var(--orange);
          color: #fff;
          box-shadow: 0 10px 30px rgba(255,90,31,0.28);
        }
        .niw-btn-ghost {
          color: var(--fg);
          border: 1px solid var(--line);
          background: transparent;
        }
        .niw-foot {
          width: min(40rem, 100%);
          margin: 2rem auto 0;
          color: rgba(245,245,244,0.42);
          font-size: 0.8rem;
          text-align: center;
        }
        .niw-foot a { color: rgba(245,245,244,0.62); text-decoration: none; }
      `}</style>
      <div className="niw-shell">
        <div className="niw-top">
          <a className="niw-brand" href="https://niwero.de" rel="noopener noreferrer">
            <span className="niw-dots" aria-hidden="true">
              <span />
              <span />
            </span>
            Niwero
          </a>
          <nav className="niw-nav" aria-label="Projekte">
            <a href="https://niwero.de/projekte/dr-zulassung" rel="noopener noreferrer">
              Dr. Zulassung
            </a>
            <a href="https://niwero.de/projekte/benks-kreativshop" rel="noopener noreferrer">
              Benks
            </a>
            <a href="https://niwero.de/projekte/ibbenks" rel="noopener noreferrer">
              IBBenks
            </a>
            <a href="https://niwero.de/projekte/h2ohero" rel="noopener noreferrer">
              H2oHero
            </a>
            <a href="https://niwero.de/projekte/restaurant-website" rel="noopener noreferrer">
              Restaurant
            </a>
            <a href="https://niwero.de/projekte/handwerk-relaunch" rel="noopener noreferrer">
              Handwerk
            </a>
            <a href="https://niwero.de/projekte/bauos" rel="noopener noreferrer">
              BauOS
            </a>
          </nav>
          <a className="niw-top-link" href="https://niwero.de/projekte" rel="noopener noreferrer">
            Alle Projekte
          </a>
        </div>
        <div className="niw-stage">
          <main className="niw-main">
            <div className="niw-pill">
              <i />
              {pill}
            </div>
            <h1>{title}</h1>
            <p>{message}</p>
            <div className="niw-actions">
              <a className="niw-btn niw-btn-primary" href="https://niwero.de" rel="noopener noreferrer">
                Weiter zu Niwero
              </a>
              <a
                className="niw-btn niw-btn-ghost"
                href="https://niwero.de/kontakt"
                rel="noopener noreferrer"
              >
                Kontakt
              </a>
            </div>
          </main>
        </div>
        <footer className="niw-foot">
          <a href="https://niwero.de/impressum" rel="noopener noreferrer">
            Impressum
          </a>
          {' · '}
          <a href="https://niwero.de/datenschutz" rel="noopener noreferrer">
            Datenschutz
          </a>
        </footer>
      </div>
    </div>
  );
}
